import { useState } from "react"

import { CalendarIcon } from "lucide-react"
import { DateTime } from "luxon"

import { Button } from "./ui/button.tsx"
import { Calendar } from "./ui/calendar.tsx"
import { Input } from "./ui/input.tsx"
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover.tsx"

export default function DateTimePicker({
  value,
  onChange,
  dateOnly,
  className,
}: {
  value: Date | undefined
  onChange: (d: Date | undefined) => void
  dateOnly?: boolean
  className?: string
}) {
  const [open, setOpen] = useState<boolean>(false)
  const time = DateTime.fromJSDate(value || new Date()).toLocaleString(
    DateTime.TIME_24_WITH_SECONDS,
  )
  const handleTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    const [hours, minutes, seconds] = e.target.value.split(":").map((x) => Number(x))
    const draft = new Date(value || new Date())
    draft.setHours(hours)
    draft.setMinutes(minutes)
    draft.setSeconds(seconds)
    onChange(draft)
  }
  return (
    <div className={`flex ${className}`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            id="date-picker"
            className={`justify-between font-normal basis-3/4 grow ${
              !dateOnly && "rounded-r-none"
            }`}
          >
            {value && value instanceof Date ? value.toLocaleDateString() : "Select date"}
            <CalendarIcon />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden p-0 z-200" align="start">
          <Calendar
            mode="single"
            selected={value}
            captionLayout="dropdown"
            defaultMonth={value}
            onSelect={(date) => {
              const [hour, minute, second] = time.split(":").map((x) => Number(x))
              const newDate = DateTime.fromJSDate(date || new Date())
                .set({
                  hour,
                  minute,
                  second,
                })
                .toJSDate()
              onChange(newDate)
              setOpen(false)
            }}
          />
        </PopoverContent>
      </Popover>
      {!dateOnly && (
        <Input
          type="time"
          id="time-picker"
          step="1"
          value={time}
          onChange={handleTime}
          className="shrink grow basis-1/4 bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none rounded-l-none border-l-0"
        />
      )}
    </div>
  )
}
